// Google Analytics 4 Integration for SmartFacture AI
// Tracks page views, signups, subscriptions, and conversions

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

// Initialize GA4
export function initializeGA() {
  if (!GA_MEASUREMENT_ID) {
    console.warn('GA4 Measurement ID not configured');
    return;
  }

  // Check if already initialized
  if (window.gtagInitialized) return;
  window.gtagInitialized = true;

  // Load the gtag script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Initialize dataLayer and gtag function
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;
  
  // Configure GA4 - disable automatic page views for SPA
  gtag('js', new Date());
  gtag('config', GA_MEASUREMENT_ID, {
    'send_page_view': false, // We track pages manually for SPA
    'transport_type': 'beacon',
    'anonymize_ip': true // RGPD compliance
  });
}

// Track page view - call this on route change
export function trackPageView(path, title) {
  if (!GA_MEASUREMENT_ID || typeof window.gtag !== 'function') return;
  
  window.gtag('config', GA_MEASUREMENT_ID, {
    page_path: path,
    page_title: title || document.title
  });
}

// Track custom events
export function trackEvent(eventName, params = {}) {
  if (typeof window.gtag !== 'function') return;
  window.gtag('event', eventName, params);
}

// Pre-defined event trackers for SmartFacture AI
export const analytics = {
  // Generic event tracking
  trackEvent: (eventName, params = {}) => {
    trackEvent(eventName, params);
  },

  // User authentication events
  trackSignup: (method = 'email') => {
    trackEvent('sign_up', { method });
  },

  trackLogin: (method = 'email') => {
    trackEvent('login', { method });
  },

  // Subscription events
  trackSubscriptionStart: (plan, value) => {
    trackEvent('purchase', {
      currency: 'EUR',
      value: value,
      items: [{
        item_id: plan.toLowerCase().replace(' ', '_'),
        item_name: plan,
        item_category: 'subscription',
        price: value,
        quantity: 1
      }]
    });
  },

  trackTrialStart: (plan) => {
    trackEvent('trial_start', {
      plan_name: plan
    });
  },

  // Funnel events
  trackPricingView: () => {
    trackEvent('view_pricing', {
      page_location: window.location.href
    });
  },

  trackCheckoutBegin: (plan, price) => {
    trackEvent('begin_checkout', {
      currency: 'EUR',
      value: price,
      items: [{
        item_name: plan,
        item_category: 'subscription',
        price: price
      }]
    });
  },

  // Feature engagement
  trackFeatureUse: (featureName) => {
    trackEvent('feature_use', {
      feature_name: featureName
    });
  },

  trackInvoiceCreated: () => {
    trackEvent('invoice_created');
  },

  trackInvoiceScanned: () => {
    trackEvent('invoice_scanned');
  },

  trackExportGenerated: (exportType) => {
    trackEvent('export_generated', {
      export_type: exportType
    });
  },

  // Guide/Lead events
  trackGuideDownload: () => {
    trackEvent('generate_lead', {
      lead_type: 'guide_download'
    });
  },

  // Button clicks
  trackButtonClick: (buttonName, section) => {
    trackEvent('button_click', {
      button_name: buttonName,
      section: section
    });
  },

  // Demo engagement
  trackDemoView: () => {
    trackEvent('demo_view');
  },

  trackDemoInteraction: (interaction) => {
    trackEvent('demo_interaction', {
      interaction_type: interaction
    });
  },

  // Blog engagement
  trackBlogView: (articleSlug, articleTitle) => {
    trackEvent('blog_article_view', {
      article_slug: articleSlug,
      article_title: articleTitle
    });
  },

  // Partner program
  trackPartnerSignup: () => {
    trackEvent('partner_signup');
  }
};

export default { initializeGA, trackPageView, trackEvent, analytics };
